import { cn } from "@/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef } from "react";

const segmentControlVariants = cva(
  "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-muted text-muted-foreground hover:bg-muted/80",
        active: "bg-background text-foreground shadow-xs",
      },
      size: {
        default: "h-9 px-3",
        sm: "h-8 px-2 text-xs",
        lg: "h-10 px-4",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface SegmentControlProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof segmentControlVariants> {
  value: string;
  onValueChange: (value: string) => void;
  options: Array<{
    value: string;
    label: string;
  }>;
}

const SegmentControl = forwardRef<HTMLDivElement, SegmentControlProps>(
  (
    { className, variant, size, value, onValueChange, options, ...props },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          "inline-flex rounded-md border bg-muted text-muted-foreground",
          className
        )}
        {...props}
      >
        {options.map((option, index) => (
          <button
            key={option.value}
            type="button"
            className={cn(
              segmentControlVariants({
                variant: value === option.value ? "active" : "default",
                size,
                className: cn(
                  "border-0 shadow-none",
                  index === 0 && "rounded-l-md rounded-r-none",
                  index === options.length - 1 && "rounded-r-md rounded-l-none",
                  index > 0 && index < options.length - 1 && "rounded-none",
                  "first:border-r last:border-l"
                ),
              })
            )}
            onClick={() => onValueChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    );
  }
);

SegmentControl.displayName = "SegmentControl";

export { SegmentControl, segmentControlVariants };
